import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { makePath } from 'utils/routes';
import { useLocation } from 'wouter';

import { IntegrationsDesigner } from './IntegrationsPageDesigner';

const IntegrationLocationContext = createContext<{
  path: string;
  navigate: (path: string) => void;
} | null>(null);

const useIntegrationLocation = ({
  base,
}: {
  base: string;
}): [string, (path: string) => void] => {
  const location = useContext(IntegrationLocationContext);
  if (!location) throw new Error('Integration location provider is required');
  const { navigate: navigateLocation } = location;
  const navigate = useCallback(
    (path: string) =>
      navigateLocation(path.startsWith('~') ? path.slice(1) : `${base}${path}`),
    [base, navigateLocation]
  );
  return [location.path.slice(base.length) || '/', navigate];
};

export const IntegrationSettingsRouter = ({
  workspaceId,
}: {
  workspaceId: string;
}) => {
  const base = makePath([
    'w',
    workspaceId,
    'projects',
    'settings',
    'integrations',
  ]);
  const [browserPath, navigateBrowser] = useLocation();
  const [path, setPath] = useState(() =>
    browserPath === base || browserPath.startsWith(`${base}/`)
      ? browserPath
      : base
  );
  const navigate = useCallback(
    (nextPath: string) => {
      if (nextPath === base || nextPath.startsWith(`${base}/`)) {
        setPath(nextPath);
      } else {
        navigateBrowser(`~${nextPath}`);
      }
    },
    [base, navigateBrowser]
  );
  // Keep the router context stable when the parent rerenders without navigation.
  const location = useMemo(() => ({ path, navigate }), [path, navigate]);
  return (
    <IntegrationLocationContext.Provider value={location}>
      <IntegrationsDesigner base={base} hook={useIntegrationLocation} />
    </IntegrationLocationContext.Provider>
  );
};
